/* MapPicker - Leaflet-based interactive location picker.
 * Click on the map → drops/moves a pin and calls onChange({lat, lng}).
 * Supports address search via Nominatim (OpenStreetMap free geocoding, no key).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, MapPin, X, Crosshair } from "lucide-react";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const TR_CENTER = [39.0, 35.0];

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FlyTo({ to, zoom = 14 }) {
  const map = useMap();
  useEffect(() => {
    if (to && Array.isArray(to)) map.flyTo(to, zoom, { duration: 0.6 });
  }, [to, zoom, map]);
  return null;
}

export default function MapPicker({ lat, lng, onChange, height = 320, defaultQuery = "" }) {
  const [pin, setPin] = useState(lat && lng ? [parseFloat(lat), parseFloat(lng)] : null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  const lastEmitted = useRef("");

  // Sync external lat/lng changes (e.g. resetting form)
  useEffect(() => {
    const t = setTimeout(() => {
      if (lat && lng) {
        const next = [parseFloat(lat), parseFloat(lng)];
        const key = `${next[0].toFixed(6)},${next[1].toFixed(6)}`;
        if (key !== lastEmitted.current) {
          setPin(next);
          setFlyTo(next);
        }
      } else if (lat === "" || lat === null) {
        setPin(null);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [lat, lng]);

  const handlePick = (la, ln) => {
    const next = [la, ln];
    setPin(next);
    lastEmitted.current = `${la.toFixed(6)},${ln.toFixed(6)}`;
    onChange({ lat: la, lng: ln });
  };

  const doSearch = async () => {
    const q = search.trim() || defaultQuery;
    if (!q) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=6&q=${encodeURIComponent(q)}&countrycodes=tr&accept-language=tr`;
      const r = await fetch(url, { headers: { "Accept": "application/json" } });
      const data = await r.json();
      setResults(data || []);
      if (data && data[0]) {
        setFlyTo([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
      }
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (r) => {
    const la = parseFloat(r.lat);
    const ln = parseFloat(r.lon);
    handlePick(la, ln);
    setFlyTo([la, ln]);
    setResults([]);
  };

  const useDeviceLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = pos.coords.latitude;
        const ln = pos.coords.longitude;
        handlePick(la, ln);
        setFlyTo([la, ln]);
      },
      () => {}
    );
  };

  const clearPin = () => {
    setPin(null);
    onChange({ lat: null, lng: null });
  };

  const center = useMemo(() => pin || TR_CENTER, [pin]);
  const initialZoom = pin ? 14 : 6;

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="map-picker">
      {/* Toolbar */}
      <div className="px-2 py-2 border-b flex flex-wrap gap-1.5 items-center bg-gray-50">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), doSearch())}
            placeholder="Adres / mekan ara (örn. Düzce Merkez)"
            className="w-full pl-7 pr-2 py-1.5 border rounded text-xs"
            data-testid="map-picker-search"
          />
        </div>
        <button type="button" onClick={doSearch} disabled={searching} className="px-3 py-1.5 bg-[#0b0b0d] text-white rounded text-xs font-semibold disabled:opacity-50">
          {searching ? "..." : "Ara"}
        </button>
        <button type="button" onClick={useDeviceLocation} className="px-2 py-1.5 border rounded text-xs flex items-center gap-1 hover:bg-white" title="Konumumu kullan">
          <Crosshair className="w-3.5 h-3.5" /> Konumum
        </button>
        {pin && (
          <button type="button" onClick={clearPin} className="px-2 py-1.5 border rounded text-xs text-red-600 flex items-center gap-1 hover:bg-red-50">
            <X className="w-3.5 h-3.5" /> Pin&#39;i kaldır
          </button>
        )}
      </div>

      {/* Search results dropdown */}
      {results.length > 0 && (
        <div className="border-b max-h-32 overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.place_id}
              type="button"
              onClick={() => pickResult(r)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-amber-50 border-b border-gray-100 flex items-start gap-1.5"
            >
              <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#e11d2a]" />
              <span className="truncate">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Map */}
      <div style={{ height }}>
        <MapContainer center={center} zoom={initialZoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={handlePick} />
          <FlyTo to={flyTo} zoom={pin ? 14 : 11} />
          {pin && <Marker position={pin} icon={icon} />}
        </MapContainer>
      </div>

      {/* Coords + tip */}
      <div className="px-3 py-2 border-t bg-gray-50 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="text-gray-500">Haritaya tıklayarak konumu seçin.</div>
        {pin ? (
          <div className="font-mono text-gray-700" data-testid="map-picker-coords">
            {pin[0].toFixed(6)}, {pin[1].toFixed(6)}
          </div>
        ) : (
          <div className="text-gray-400">Konum seçilmedi</div>
        )}
      </div>
    </div>
  );
}
